<template>
  <v-dialog
    :value="value"
    max-width="1440"
    content-class="permissions-dialog-frame"
    :persistent="saving"
    @input="$emit('input', $event)"
  >
    <v-card class="permissions-dialog" aria-labelledby="permissions-dialog-title">
      <div class="permissions-dialog__header">
        <div class="permissions-dialog__heading">
          <h2 id="permissions-dialog-title" class="text-h6">Permisos de {{ adminName }}</h2>
          <p class="permissions-dialog__muted text-caption mb-0">
            Elige qué secciones ve esta persona y qué puede hacer en cada una.
          </p>
        </div>
        <v-btn icon aria-label="Cerrar" :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="permissions-dialog__toolbar">
        <div class="permissions-dialog__profile">
          <v-select
            :value="selectedProfileKey"
            :items="profiles"
            item-text="label"
            item-value="key"
            label="Perfil"
            placeholder="Personalizado"
            persistent-placeholder
            dense
            outlined
            hide-details
            background-color="white"
            :disabled="saving"
            @change="applyProfile"
          ></v-select>
          <p class="permissions-dialog__muted text-caption mb-0">
            Aplicar un perfil reemplaza los permisos actuales. Si luego cambias algo, pasa a
            Personalizado.
          </p>
        </div>
        <div class="permissions-dialog__super">
          <v-icon color="primary" class="mr-3">mdi-shield-check-outline</v-icon>
          <v-switch
            :input-value="hasSuperAdmin"
            :label="superAdminLabel"
            :hint="superAdminHint"
            persistent-hint
            inset
            dense
            class="mt-0 pt-0"
            :disabled="saving"
            @change="toggle(superAdminTag, $event)"
          ></v-switch>
        </div>
      </div>

      <v-alert v-if="!groups.length" dense outlined type="info" class="ma-4">
        El catálogo de permisos no trae secciones. Recarga la página o contacta a soporte.
      </v-alert>

      <div v-else class="permissions-dialog__body">
        <nav class="permissions-dialog__nav" aria-label="Menús y secciones">
          <div class="permissions-dialog__nav-title text-overline">Menús del panel</div>
          <div class="permissions-dialog__nav-scroll">
            <v-list dense nav class="py-0">
              <template v-for="group in groups">
                <v-list-item
                  :key="`group-${group.key}`"
                  class="permissions-dialog__group"
                  :aria-expanded="String(isGroupOpen(group.key))"
                  @click="toggleGroup(group)"
                >
                  <v-icon small class="mr-2" :class="{ 'chevron--open': isGroupOpen(group.key) }">
                    mdi-chevron-right
                  </v-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">{{ group.title }}</v-list-item-title>
                  </v-list-item-content>
                  <span class="permissions-dialog__counter">
                    {{ groupSummary(group).active }}/{{ groupSummary(group).total }}
                  </span>
                  <span
                    :class="['status-dot', `status-dot--${groupSummary(group).status}`]"
                    aria-hidden="true"
                  ></span>
                  <span class="d-sr-only">{{ statusLabels[groupSummary(group).status] }}</span>
                </v-list-item>
                <template v-if="isGroupOpen(group.key)">
                  <v-list-item
                    v-for="section in group.sections"
                    :key="`section-${section.key}`"
                    class="permissions-dialog__section"
                    :input-value="section.key === selectedSectionKey"
                    :aria-current="section.key === selectedSectionKey ? 'true' : 'false'"
                    color="primary"
                    @click="selectSection(section.key)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ section.label }}</v-list-item-title>
                    </v-list-item-content>
                    <span class="permissions-dialog__counter">
                      {{ sectionSummary(section).active }}/{{ sectionSummary(section).total }}
                    </span>
                    <span
                      :class="['status-dot', `status-dot--${sectionSummary(section).status}`]"
                      aria-hidden="true"
                    ></span>
                    <span class="d-sr-only">{{ statusLabels[sectionSummary(section).status] }}</span>
                  </v-list-item>
                </template>
              </template>
            </v-list>
          </div>
          <div class="permissions-dialog__legend text-caption">
            <span v-for="(label, status) in statusLabels" :key="status" class="d-flex align-center">
              <span :class="['status-dot', `status-dot--${status}`, 'mr-1']" aria-hidden="true"></span>
              {{ label }}
            </span>
          </div>
        </nav>

        <section
          v-if="selectedSection"
          class="permissions-dialog__detail"
          aria-labelledby="permissions-detail-title"
        >
          <div class="permissions-dialog__muted text-caption">
            Menú {{ selectedGroup.title }} › {{ selectedSection.label }}
          </div>
          <div class="d-flex align-start flex-wrap">
            <div class="flex-grow-1 mr-4 permissions-dialog__detail-heading">
              <h3 id="permissions-detail-title" class="text-h6">{{ selectedSection.label }}</h3>
              <p class="permissions-dialog__muted text-body-2 mb-0">{{ selectedDescription }}</p>
            </div>
            <div class="d-flex flex-column align-end">
              <v-chip
                small
                :class="[
                  'mb-2',
                  `count-pill--${selectedSummary.status}`,
                  { 'green--text text--darken-4': selectedSummary.status === 'full' }
                ]"
              >
                {{ selectedSummary.active }} de {{ selectedSummary.total }}
                {{ selectedSummary.total === 1 ? 'acción activa' : 'acciones activas' }}
              </v-chip>
              <div>
                <v-btn
                  small
                  outlined
                  class="mr-1"
                  :disabled="saving || hasSuperAdmin || selectedSummary.status === 'full'"
                  @click="toggleMany(selectedTags, true)"
                >
                  Activar todas
                </v-btn>
                <v-btn
                  small
                  outlined
                  :disabled="saving || hasSuperAdmin || selectedSummary.status === 'none'"
                  @click="toggleMany(selectedTags, false)"
                >
                  Quitar todas
                </v-btn>
              </div>
            </div>
          </div>

          <v-alert
            v-if="hasSuperAdmin"
            dense
            text
            type="success"
            icon="mdi-shield-check"
            role="status"
            class="mt-4 mb-0"
          >
            <span class="green--text text--darken-4">
              Acceso total activo: esta persona tiene todas las acciones de todas las secciones.
              Desactívalo para editar permisos por sección.
            </span>
          </v-alert>

          <v-alert
            v-if="!selectedActions.view && kindGroups.length"
            dense
            outlined
            icon="mdi-information-outline"
            class="permissions-dialog__notice text-caption mt-4 mb-0"
          >
            Esta sección no tiene permiso Ver: cada acción se concede por separado.
          </v-alert>

          <div v-for="group in kindGroups" :key="group.kind" class="permissions-dialog__kind">
            <div class="permissions-dialog__subtitle">
              <h4 class="text-overline">{{ group.label }}</h4>
              <span class="permissions-dialog__muted text-caption ml-2">
                {{ kindSummary(group).active }}/{{ kindSummary(group).total }}
              </span>
            </div>
            <v-sheet outlined rounded :class="kindSheetClass(group)">
              <template v-for="row in group.rows">
                <div
                  v-if="row.action"
                  :key="row.key"
                  class="action-row"
                  :class="{ 'action-row--nested': row.depth > 0 }"
                  :style="row.depth > 1 ? { paddingLeft: `${16 + row.depth * 28}px` } : null"
                >
                  <v-switch
                    :input-value="isChecked(row.action)"
                    :label="row.action.label"
                    :hint="actionHelper(row.action, selectedSection.actions)"
                    persistent-hint
                    inset
                    dense
                    class="action-row__switch"
                    :disabled="saving || hasSuperAdmin"
                    @change="toggle(row.action.tag, $event)"
                  ></v-switch>
                  <v-chip
                    v-if="row.action === selectedActions.view"
                    x-small
                    label
                    color="primary"
                    class="action-row__kind"
                  >
                    Ver · acceso a la sección
                  </v-chip>
                </div>
                <div v-else :key="row.key" class="action-row">
                  <div class="action-row__scope">
                    <div class="action-row__label">{{ row.pair.label }}</div>
                    <v-btn-toggle
                      :value="pairScope(row.pair)"
                      mandatory
                      dense
                      color="primary"
                      role="group"
                      class="my-1"
                      :aria-label="`Alcance: ${row.pair.label}`"
                      @change="changeScope(row.pair, $event)"
                    >
                      <v-btn
                        v-for="option in scopeOptions"
                        :key="option.value"
                        :value="option.value"
                        small
                        :disabled="saving || hasSuperAdmin || !isScopeAllowed(row.pair, option.value)"
                      >
                        {{ option.label }}
                      </v-btn>
                    </v-btn-toggle>
                    <div class="action-row__helper text-caption">{{ scopedPairHelper(row.pair) }}</div>
                    <div v-if="!isScopeAllowed(row.pair, scopeAll)" class="action-row__helper text-caption">
                      "Todos" requiere ver todos los registros de la sección.
                    </div>
                  </div>
                  <v-chip
                    v-if="row.pair.general === selectedActions.view"
                    x-small
                    label
                    color="primary"
                    class="action-row__kind"
                  >
                    Ver · acceso a la sección
                  </v-chip>
                </div>
              </template>
            </v-sheet>
            <template v-if="group.kind === 'view' && selectedActions.view">
              <p
                v-if="selectedActions.dependents.length"
                class="permissions-dialog__muted text-caption mt-2 mb-0"
              >
                {{
                  selectedActions.independent.length
                    ? 'Las demás acciones requieren Ver, salvo las marcadas "No requiere Ver".'
                    : 'Las demás acciones de esta sección requieren Ver.'
                }}
              </p>
              <v-alert
                v-if="selectedActions.dependents.length && !viewOn"
                dense
                outlined
                icon="mdi-information-outline"
                class="permissions-dialog__notice text-caption mt-2 mb-0"
              >
                Ver está desactivado. Si activas una acción que requiere Ver, Ver se activará
                automáticamente.
              </v-alert>
            </template>
          </div>
          <p v-if="isReadOnly" class="permissions-dialog__muted text-body-2 mt-4 mb-0">
            Esta sección es de solo lectura: no tiene acciones adicionales.
          </p>
        </section>
        <section
          v-else-if="groups.length"
          class="permissions-dialog__detail permissions-dialog__empty d-flex flex-column align-center justify-center text-center pa-8"
        >
          <v-icon large color="primary" class="mb-3" aria-hidden="true">mdi-gesture-tap</v-icon>
          <h3 class="text-subtitle-1 font-weight-medium mb-1">Elige una sección</h3>
          <p class="text-body-2 permissions-dialog__muted mb-0">
            Abre un menú a la izquierda y elige una sección para ver y asignar sus permisos.
          </p>
        </section>
      </div>

      <v-alert v-if="error" dense type="error" class="mx-4 my-2">{{ error }}</v-alert>
      <v-alert v-if="savedNotice" dense text type="success" class="mx-4 my-2" role="status">
        Permisos guardados.
      </v-alert>

      <div class="permissions-dialog__footer">
        <div class="d-flex align-center flex-wrap" role="status" aria-live="polite">
          <v-chip small color="primary" class="mr-2 font-weight-bold">{{ activeCount }}</v-chip>
          <span class="text-body-2">
            <strong>{{ activeCount === 1 ? 'permiso activo' : 'permisos activos' }}</strong>
            <span class="permissions-dialog__muted"> · {{ profileLabel }}</span>
          </span>
          <v-chip small outlined class="ml-3">
            <v-icon v-if="changeCount" x-small left color="warning">mdi-circle</v-icon>
            {{ changeLabel }}
          </v-chip>
        </div>
        <v-spacer></v-spacer>
        <v-btn text :disabled="saving" @click="close">{{ changeCount ? 'Cancelar' : 'Cerrar' }}</v-btn>
        <v-btn color="primary" depressed :loading="saving" :disabled="!changeCount" @click="save">Guardar</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import EjecutivosService from '../services/ejecutivos.service';
import { SUPER_ADMIN_TAG, permissionErrorMessage } from '../utils/permissions';
import {
  actionHelper,
  countActivePermissions,
  countChanges,
  findMatchingProfile,
  getMatrixGroups,
  getSuperAdminLabel,
  grantSummary,
  groupActionsByKind,
  isViewOn,
  rowTags,
  scopeOf,
  scopedPairHelper,
  sectionDescription,
  sectionUnits,
  setScope,
  SCOPES,
  setTags,
  splitSectionActions,
  tagLabels,
  toggleTag,
  withoutRedundantOwn,
  knownTags,
  canScopeAll
} from '../utils/admin-permissions.utils';

const STATUS_LABELS = { none: 'Sin acceso', partial: 'Parcial', full: 'Completo' };
const SCOPE_OPTIONS = [
  { value: SCOPES.none, label: 'No' },
  { value: SCOPES.own, label: 'Solo los propios' },
  { value: SCOPES.all, label: 'Todos' }
];

/**
 * Edits the full tag list of another administrator (spec 132) as a
 * master-detail view: menu groups and sections on the left, the selected
 * section's actions on the right. The backend replaces the whole list, so
 * applying a profile just loads its tags here. Turning an action on adds its
 * `requires` ("Ver") unless a Ver variant (`Ver` or `Ver-own`) is already on;
 * turning the last Ver variant off drops the actions that need it.
 */
export default {
  name: 'AdminPermissionsDialog',
  props: {
    value: { type: Boolean, default: false },
    // { id, name, surname, permissions }
    admin: { type: Object, default: null },
    // { permissions, sections: [{ key, label, group, actions }], profiles }
    catalog: { type: Object, default: null }
  },
  data() {
    return {
      selected: [],
      // Per-section tags kept while "Acceso total" is on, restored when it is turned off.
      tagsBeforeSuperAdmin: null,
      // Last stored tags: the "unsaved changes" baseline, moved forward on each save.
      savedPermissions: [],
      savedNotice: false,
      saving: false,
      error: null,
      openGroups: [],
      selectedSectionKey: null,
      superAdminTag: SUPER_ADMIN_TAG,
      statusLabels: STATUS_LABELS,
      scopeOptions: SCOPE_OPTIONS,
      scopeAll: SCOPES.all
    };
  },
  computed: {
    adminName() {
      return this.admin ? `${this.admin.name} ${this.admin.surname || ''}`.trim() : '';
    },
    superAdminLabel() {
      return getSuperAdminLabel(this.catalog);
    },
    superAdminHint() {
      return this.hasSuperAdmin
        ? 'Concede todas las acciones de todas las secciones.'
        : 'Desactivado: se usan los permisos por sección.';
    },
    profiles() {
      return (this.catalog && this.catalog.profiles) || [];
    },
    groups() {
      return getMatrixGroups(this.catalog);
    },
    hasSuperAdmin() {
      return this.selected.includes(SUPER_ADMIN_TAG);
    },
    selectedProfileKey() {
      const profile = findMatchingProfile(this.selected, this.catalog);
      return profile ? profile.key : null;
    },
    profileLabel() {
      const profile = findMatchingProfile(this.selected, this.catalog);
      return profile ? profile.label : 'Personalizado';
    },
    selectedGroup() {
      return (
        this.groups.find((group) =>
          group.sections.some((section) => section.key === this.selectedSectionKey)
        ) || null
      );
    },
    selectedSection() {
      if (!this.selectedGroup) return null;
      return this.selectedGroup.sections.find((s) => s.key === this.selectedSectionKey);
    },
    selectedDescription() {
      return sectionDescription(this.selectedGroup);
    },
    selectedTags() {
      return rowTags(this.selectedSection);
    },
    // A scoped pair (`X` + `X-own`) counts as one action in every counter.
    selectedSummary() {
      return grantSummary(this.selected, sectionUnits(this.selectedSection));
    },
    selectedActions() {
      return splitSectionActions(this.selectedSection);
    },
    // "Ver" is on in any variant ("Todos" or "Solo los propios"), or through `*`.
    viewOn() {
      return isViewOn(this.selected, this.selectedActions);
    },
    // A section with "Ver" and nothing else.
    isReadOnly() {
      const { view, dependents, independent } = this.selectedActions;
      return Boolean(view) && !dependents.length && !independent.length;
    },
    // Subsections of the detail pane by action kind ("Ver", "Crear"...), with
    // the special actions listed under the kind they refine.
    kindGroups() {
      return groupActionsByKind(this.selectedSection);
    },
    activeCount() {
      return countActivePermissions(this.selected, this.catalog);
    },
    changeCount() {
      return countChanges(this.savedPermissions, this.selected);
    },
    changeLabel() {
      if (!this.changeCount) return 'Sin cambios';
      return this.changeCount === 1 ? '1 cambio sin guardar' : `${this.changeCount} cambios sin guardar`;
    }
  },
  watch: {
    selected() {
      this.savedNotice = false;
    },
    value: {
      immediate: true,
      handler(open) {
        if (open) this.reset();
      }
    }
  },
  methods: {
    actionHelper,
    scopedPairHelper,
    /** "Todos" is off while the section's Ver is only the own records (correlative scopes). */
    isScopeAllowed(pair, scope) {
      return scope !== SCOPES.all || canScopeAll(this.selected, pair, this.catalog);
    },
    reset() {
      // Tags removed from the catalog are dropped here, so a save cannot fail on them.
      this.selected = knownTags((this.admin && this.admin.permissions) || [], this.catalog);
      this.savedPermissions = [...this.selected];
      this.tagsBeforeSuperAdmin = null;
      this.savedNotice = false;
      this.error = null;
      this.saving = false;
      // Opens with nothing expanded or selected: the person picks where to start.
      this.openGroups = [];
      this.selectedSectionKey = null;
    },
    close() {
      this.$emit('input', false);
    },
    isGroupOpen(key) {
      return this.openGroups.includes(key);
    },
    /** Expands or collapses a group; opening one also shows its first section. */
    toggleGroup(group) {
      if (this.isGroupOpen(group.key)) {
        this.openGroups = this.openGroups.filter((key) => key !== group.key);
        return;
      }
      this.openGroups = [...this.openGroups, group.key];
      if (!this.selectedGroup || this.selectedGroup.key !== group.key) {
        this.selectedSectionKey = group.sections[0].key;
      }
    },
    selectSection(key) {
      this.selectedSectionKey = key;
    },
    kindSummary(group) {
      return grantSummary(this.selected, group.units);
    },
    // The "Ver" subsection keeps the highlighted card of the section's Ver action.
    kindSheetClass(group) {
      const { view } = this.selectedActions;
      if (group.kind !== 'view' || !view) return [];
      return ['permissions-dialog__view', { 'permissions-dialog__view--on': this.viewOn }];
    },
    sectionSummary(section) {
      return grantSummary(this.selected, sectionUnits(section));
    },
    groupSummary(group) {
      return grantSummary(this.selected, group.sections.flatMap((section) => sectionUnits(section)));
    },
    applyProfile(key) {
      const profile = this.profiles.find((p) => p.key === key);
      if (!profile) return;
      const tags = [...(profile.permissions || [])];
      if (tags.includes(SUPER_ADMIN_TAG) && !this.hasSuperAdmin) {
        this.tagsBeforeSuperAdmin = [...this.selected];
      }
      this.selected = tags;
    },
    // `*` grants every tag: every switch shows on and is locked.
    isChecked(action) {
      return this.selected.includes(action.tag) || this.hasSuperAdmin;
    },
    setSuperAdmin(checked) {
      if (checked) {
        this.tagsBeforeSuperAdmin = this.selected.filter((t) => t !== SUPER_ADMIN_TAG);
        this.selected = [SUPER_ADMIN_TAG];
        return;
      }
      const previous = this.tagsBeforeSuperAdmin || this.selected;
      this.selected = previous.filter((t) => t !== SUPER_ADMIN_TAG);
      this.tagsBeforeSuperAdmin = null;
    },
    toggle(tag, checked) {
      if (tag === SUPER_ADMIN_TAG) {
        this.setSuperAdmin(Boolean(checked));
        return;
      }
      this.selected = toggleTag(this.selected, tag, Boolean(checked), this.catalog);
    },
    // "Activar todas" picks "Todos" for scoped pairs: the general tag, never both.
    toggleMany(tags, checked) {
      const next = setTags(this.selected, tags, checked, this.catalog);
      this.selected = checked ? withoutRedundantOwn(next, this.catalog) : next;
    },
    pairScope(pair) {
      return scopeOf(this.selected, pair);
    },
    changeScope(pair, scope) {
      this.selected = setScope(this.selected, pair, scope, this.catalog);
    },
    errorMessage(res) {
      if (res.code === 'USER_ADMIN_NOT_FOUND') return 'El administrador ya no existe.';
      const message = permissionErrorMessage(res, 'No se pudieron guardar los permisos.');
      if (res.code !== 'MISSING_VIEW_PERMISSION' || !(res.missing || []).length) return message;
      return `${message} Faltan: ${tagLabels(res.missing, this.catalog).join(', ')}.`;
    },
    async save() {
      this.saving = true;
      this.error = null;
      const res = await EjecutivosService.updatePermissions(this.admin.id, this.selected);
      this.saving = false;
      if (res.status === 'ok') {
        // The dialog stays open so more changes can follow; the stored list
        // becomes the new baseline.
        const stored = res.data && Array.isArray(res.data.permissions) ? res.data.permissions : this.selected;
        this.selected = [...stored];
        this.savedPermissions = [...stored];
        this.$nextTick(() => {
          this.savedNotice = true;
        });
        this.$emit('saved', res.data);
        return;
      }
      this.error = this.errorMessage(res);
    }
  }
};
</script>

<style scoped>
.permissions-dialog {
  display: flex;
  flex-direction: column;
  height: min(920px, calc(100vh - 32px));
  overflow: hidden;
}

.permissions-dialog__header,
.permissions-dialog__toolbar,
.permissions-dialog__footer {
  flex: none;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 24px;
}

.permissions-dialog__header {
  justify-content: space-between;
  border-bottom: 1px solid var(--pw-border);
}

.permissions-dialog__heading h2 {
  margin: 0;
}

.permissions-dialog__toolbar {
  flex-wrap: wrap;
  background: var(--pw-bg);
  border-bottom: 1px solid var(--pw-border);
}

.permissions-dialog__profile {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 420px;
  min-width: 0;
}

.permissions-dialog__profile .v-select {
  flex: 0 0 240px;
}

.permissions-dialog__super {
  display: flex;
  align-items: center;
  margin-left: auto;
  padding: 8px 14px;
  border: 1px solid var(--pw-border);
  border-radius: var(--pw-radius-control);
  background: var(--pw-surface);
}

.permissions-dialog__muted {
  color: var(--pw-text-muted);
}

.permissions-dialog__body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.permissions-dialog__nav {
  flex: 0 0 360px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--pw-border);
}

.permissions-dialog__nav-title {
  padding: 10px 16px 4px;
  color: var(--pw-text-muted);
}

.permissions-dialog__nav-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 8px;
}

.permissions-dialog__section {
  padding-left: 34px;
}

.chevron--open {
  transform: rotate(90deg);
}

.permissions-dialog__counter {
  margin: 0 8px;
  font-size: 12px;
  color: var(--pw-text-muted);
  font-variant-numeric: tabular-nums;
}

.permissions-dialog__legend {
  flex: none;
  display: flex;
  gap: 16px;
  padding: 10px 16px;
  border-top: 1px solid var(--pw-border);
  color: var(--pw-text-muted);
}

.status-dot {
  display: inline-block;
  flex: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid var(--pw-text-muted);
  background: var(--pw-surface);
}

.status-dot--partial {
  border-color: var(--pw-primary);
  background: var(--pw-primary);
  box-shadow: inset 0 0 0 2px var(--pw-surface);
}

.status-dot--full {
  border-color: var(--pw-success);
  background: var(--pw-success);
}

.permissions-dialog__detail {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 20px 28px 24px;
}

.permissions-dialog__detail-heading {
  min-width: 0;
}

.permissions-dialog__detail-heading h3 {
  margin: 0;
}

.count-pill--none {
  background: var(--pw-divider) !important;
  color: var(--pw-text) !important;
}

.count-pill--partial {
  background: var(--pw-primary-tint) !important;
  color: var(--pw-primary) !important;
}

.count-pill--full {
  background: var(--pw-success-tint) !important;
}

.permissions-dialog__view--on {
  border-color: var(--pw-primary) !important;
  background: var(--pw-primary-tint-2);
}

.permissions-dialog__subtitle {
  display: flex;
  align-items: center;
  margin: 18px 0 8px;
  color: var(--pw-text-muted);
}

.permissions-dialog__subtitle h4 {
  margin: 0;
}

.permissions-dialog__notice {
  color: var(--pw-text) !important;
  border-color: var(--pw-border) !important;
  background: var(--pw-bg) !important;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 62px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--pw-divider);
}

/* A row that requires the row above it: indented under its parent. */
.action-row--nested {
  padding-left: 44px;
}

.action-row:last-child {
  border-bottom: 0;
}

.action-row__switch {
  flex: 1;
  min-width: 0;
  margin-top: 0;
  padding-top: 0;
}

.action-row__switch ::v-deep .v-messages__message {
  color: var(--pw-text-muted);
}

.action-row__kind {
  flex: none;
}

.action-row__scope {
  flex: 1;
  min-width: 0;
}

.action-row__helper {
  color: var(--pw-text-muted);
}

.permissions-dialog__footer {
  flex-wrap: wrap;
  border-top: 1px solid var(--pw-border);
}

@media (max-width: 760px) {
  .permissions-dialog__body {
    flex-direction: column;
  }

  .permissions-dialog__nav {
    flex: 0 0 40%;
    border-right: 0;
    border-bottom: 1px solid var(--pw-border);
  }

  .permissions-dialog__profile {
    flex-wrap: wrap;
  }
}
</style>

<style>
/* The dialog content is rendered outside this component, so scoped styles do not
   reach it. Let the card use the screen height minus 16px per side, with the panes
   (not the dialog) scrolling. */
.v-dialog.permissions-dialog-frame {
  margin: 16px;
  max-height: calc(100vh - 32px);
  overflow: hidden;
}
</style>
